import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {TextInput} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import AntibodyChainLegend from 'antibodies/antibody-map/AntibodyChainLegend';
import {useVeritasFormatPreviewQuery} from 'antibodies/format/useVeritasFormatPreviewQuery';
import Antibody from 'antibodies/visualization/Antibody';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {CenteredSpinner} from 'ui-library/Spinner';

export interface AntibodyFormatCreationModalProps {
  onRequestClose: () => void;
}

export default function AntibodyFormatCreationModal({
  onRequestClose,
}: AntibodyFormatCreationModalProps): JSX.Element {
  const [veritasName, setVeritasName] = useState('');
  const [previewName, setPreviewName] = useState<string | null>(null);

  const {status, result} = useVeritasFormatPreviewQuery(previewName);

  const handlePreview = (): void => {
    setPreviewName(veritasName);
  };

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && veritasName.trim().length > 0) {
      handlePreview();
    }
  };

  return (
    <Modal
      buttons={[
        {
          text: 'Submit',
          submit: true,
          disabled: true,
        },
      ]}
      expandHeight={true}
      large={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {}}
      title="Create antibody format"
    >
      <Row>
        <Col sm={8}>
          <FormGroup>
            <FieldLabel label="VERITAS format name" isRequired={true} htmlFor="veritas-format-name" />
            <TextInput
              id="veritas-format-name"
              autoFocus={true}
              onChange={(e) => {
                setVeritasName(e.target.value);
              }}
              onKeyDown={handleKeyPress}
              placeholder="Enter VERITAS format name (e.g., Fc-scFv, [Fd:LC*scFv]-CH3)"
              value={veritasName}
            />
          </FormGroup>
        </Col>
        <Col sm={4}>
          <FormGroup>
            <FieldLabel label="&nbsp;" />
            <Button
              disabled={veritasName.trim().length === 0}
              onClick={handlePreview}
              text="Preview"
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.SMALL}
            />
          </FormGroup>
        </Col>
      </Row>

      {previewName != null && (
        <>
          <hr />
          <div className={css(styles.sectionTitle)}>Preview</div>
          {status === QueryResultType.PENDING && <CenteredSpinner />}
          {status === QueryResultType.ERROR && (
            <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to preview format." />
          )}
          {status === QueryResultType.SUCCESS &&
            result != null &&
            result.__typename === 'VeritasFormatPreviewErrorNode' && (
              <InlineBanner
                bannerType={InlineBannerType.ERROR}
                text={`Unable to preview format: ${result.message}`}
              />
            )}
          {status === QueryResultType.SUCCESS &&
            result != null &&
            result.__typename === 'AntibodyFormatNode' && (
              <div
                className={css(styles.previewContainer)}
                {...getTestProps({component: 'AntibodyFormatCreationModal', element: 'preview-container'})}
              >
                <AntibodyChainLegend antibodyFormat={result} />
                <Antibody format={result} showLabels={true} />
              </div>
            )}
        </>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  sectionTitle: {
    ...FONT_PROXIMA_12,
    color: Color.GRAY_600,
    fontWeight: 600,
    textTransform: 'uppercase',
    marginBottom: '16px',
  },
  previewContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '500px',
    width: '800px',
    padding: '20px',
  },
});
